import React from 'react';
import styled, { css } from 'styled-components';
import VehicleIcon from '../../../../common/src/assets/images/VehicleIcon';
import HomeIcon from '../../../../common/src/assets/images/HomeIcon';
import FamilyIcon from '../../../../common/src/assets/images/FamilyIcon';
import LegalIcon from '../../../../common/src/assets/images/LegalIcon';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';

// Components
import { H4 } from 'wg-fe-ui';
import { ToastSeverity } from '../../../../../types/Common';
import { toastForSeverity } from '../../services/productFactoryService';
import FuneralIcon from '../../../../common/src/assets/images/FuneralIcon';
import { Badge, Text } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';

interface Props {
  title?: string;
  type: string;
  selected: boolean;
  onSelected: () => void;
  imgSrc?: string | null;
  icon?: React.ReactElement;
  disabled?: boolean;
  disableMessage?: string;
  disableSeverity?: ToastSeverity;
  smallImage?: boolean;
  mandatory?: boolean;
  BottomComp?: React.ReactElement;
}

const SelectionItem: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  type,
  imgSrc,
  selected,
  onSelected,
  children,
  icon: Icon,
  disabled,
  disableMessage,
  disableSeverity,
  smallImage,
  mandatory,
  BottomComp,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const handleImage = (name: string) => {
    switch (name) {
      case 'car':
        return <VehicleIcon />;
      case 'home':
        return <HomeIcon />;
      case 'family':
        return <FamilyIcon />;
      case 'legal':
        return <LegalIcon />;
      case 'funeral':
        return <FuneralIcon />;
      default:
        return null;
    }
  };

  const ImageComponent = handleImage(type);

  const handleOnClick = () => {
    if (disabled && disableMessage)
      toastForSeverity(disableMessage, disableSeverity);
    else if (!disabled) onSelected();
  };

  return (
    <Wrapper
      data-test-id="ProdFac_SelectionItem"
      onClick={handleOnClick}
      selected={selected}
      disabled={disabled}
      {...otherProps}
    >
      {mandatory && (
        <Badge
          variant="primary"
          textStyle="extraSmallBody"
          position="absolute"
          top="2"
          right="2"
        >
          {t('Required')}
        </Badge>
      )}

      <TopContainer horizontal={!!Icon}>
        {(imgSrc || ImageComponent) && (
          <LargeIconContainer smallImage={smallImage}>
            {imgSrc ? <img src={imgSrc} alt={type} /> : ImageComponent}
          </LargeIconContainer>
        )}
        {Icon && <IconContainer>{Icon}</IconContainer>}
        <TextWrapper>
          {title && (
            <StyledH4 mandatory={mandatory} horizontal={!!Icon}>
              {title}
            </StyledH4>
          )}
          <Text textStyle="smallBody" color="text.gray">
            {children}
          </Text>
        </TextWrapper>
      </TopContainer>
      {BottomComp && <BottomContainer>{BottomComp}</BottomContainer>}
    </Wrapper>
  );
};

const TextWrapper = styled.div`
  > h4 {
    font-weight: 500;
    font-size: 2rem;

    margin-top: 2.4rem;
    line-height: 135%;
    margin-bottom: 0.4rem;
  }
`;

const BottomContainer = styled.div`
  padding: 2.4rem;
  border-top: 1px solid #f0f1f3;
`;

const StyledH4 = styled(H4)<{ horizontal?: boolean; mandatory?: boolean }>`
  display: flex;
  align-items: center;
  ${mq.mobileS} {
    /* Design system overrides if not using important */
    margin-top: 0.8rem !important;
  }
  margin-right: ${({ mandatory }) => (mandatory ? '7.2rem' : 0)};

  ${({ horizontal }) =>
    horizontal &&
    css`
      /* Design system overrides if not using important */
      margin-top: 0.8rem !important;
    `};
`;

const IconContainer = styled.div`
  display: none;
  ${mq.mobileM} {
    display: block;
  }
  margin-right: 1.6rem;
`;

const LargeIconContainer = styled.div<{ smallImage?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 1.6rem;

  > img,
  svg {
    width: ${({ smallImage }) => (smallImage ? '12rem' : '100%')};
    height: ${({ smallImage }) => (smallImage ? '5rem' : '10rem')};
    object-fit: contain;
  }
`;

const TopContainer = styled.div<{ horizontal?: boolean }>`
  display: ${({ horizontal }) => (horizontal ? 'flex' : 'block')};
  align-items: center;
  height: 100%;
  width: 100%;
  padding: ${({ horizontal }) => (horizontal ? '1.6rem' : '2.4rem')};

  > svg {
    margin: 0 auto;
    width: 100%;
  }
`;

const Wrapper = styled.div<{ selected: boolean; disabled?: boolean }>`
  position: relative;
  width: 100%;
  border-radius: 1.5rem;
  background: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 0 1px rgba(0, 0, 0, 0.04);
  border: ${({ selected, theme }) =>
    selected ? `1px solid ${theme.primary[500]}` : '1px solid #f0f1f3'};

  transition: box-shadow 0.2s;

  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  ${({ disabled }) =>
    !disabled &&
    css`
      &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08), 0 0 2px rgba(0, 0, 0, 0.1),
          0 0 1px rgba(0, 0, 0, 0.06);
      }
    `}
`;

export default SelectionItem;
